import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable } from 'rxjs';
import { Dao } from '../dao';

/** Une personne physique du foyer, telle que la photo l'a figée. */
export interface AdequacyPersonSnapshot {
  ownerId: number;
  role?: 'MAIN' | 'SPOUSE';
  profession?: string;
  professionalSituation?: string;
  totalIncome?: number;
  financialKnowledgeLabel?: string;
  riskProfileLabel?: string;
  sustainabilityLabel?: string;
  riskToleranceLabel?: string;
  lossToleranceLabel?: string;
}

/** Une société du foyer : profil de risque et encours, sans connaissance client. */
export interface AdequacyLegalEntitySnapshot {
  ownerId: number;
  denomination?: string;
  riskProfileLabel?: string;
  sustainabilityLabel?: string;
  holdings?: number;
}

/** Situation du foyer à l'instant de la photo ; `netWorth` vaut `grossWorth` moins `liabilities`. */
export interface AdequacyHouseholdSnapshot {
  maritalSituation?: string;
  childDependentCount?: number;
  persons?: AdequacyPersonSnapshot[];
  objectives?: string[];
  investmentHorizon?: string;
  totalIncome?: number;
  charges?: number;
  grossWorth?: number;
  liabilities?: number;
  netWorth?: number;
  availableCash?: number;
}

/** La photo figée à l'ouverture de la campagne. Les montants y sont déjà arrondis au centime. */
export interface AdequacyProfileSnapshot {
  schemaVersion?: number;
  capturedAt?: string;
  household?: AdequacyHouseholdSnapshot;
  legalEntities?: AdequacyLegalEntitySnapshot[];
}

export type AdequacyReviewStatus =
  | 'DRAFT'
  | 'PENDING_CLIENT'
  | 'REMINDED'
  | 'CLOSED'
  | 'REPORTED'
  | 'CANCELLED';

export interface AdequacyReviewDto {
  id: number;
  accountId: number;
  status: AdequacyReviewStatus;
  outcome?: 'CONFIRMED_UNCHANGED' | 'UPDATED' | 'FORCED_NO_ANSWER';
  periodStart?: string;
  periodEnd?: string;
  sentDate?: string;
  reminderDate?: string;
  closedDate?: string;
  snapshotBeforeAt?: string;
  snapshotAfterAt?: string;
  reportIssued: boolean;
  /** Le client a déclaré un changement ; son parcours de mise à jour est en cours. */
  changeDeclared: boolean;
  /** La personne connectée a déjà répondu pour elle-même. */
  answeredByMe: boolean;
  /** La campagne attend encore la réponse d'un autre membre du foyer. */
  awaitingHousehold: boolean;
  /** La personne connectée signe le rapport et se connecte par son propre compte : elle peut répondre. */
  canAnswer: boolean;
  /** Personne du foyer qui consulte, pour que l'écran sache laquelle des photos est la sienne. */
  viewerOwnerId: number;
  snapshotBefore?: AdequacyProfileSnapshot;
}

/**
 * Campagne d'évaluation périodique de l'adéquation, vue de l'espace client. Titulaire et conjoint y
 * accèdent avec leur propre identifiant ; le serveur remonte au compte du foyer.
 */
@Injectable({
  providedIn: 'root',
})
export class AdequacyReviewDao extends Dao {
  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  onUserChange(_event: any) {
    // Aucun cache à vider.
  }

  /** Campagne en cours du foyer, ou `null`. Volontairement non mise en cache. */
  getCurrent(): Observable<AdequacyReviewDto | null> {
    return this.command(user =>
      this.httpApi.get(`/users/${user.id}/adequacy-review/current`),
    );
  }

  /** Clôt la campagne sur confirmation que rien n'a changé. */
  confirmUnchanged(reviewId: number): Observable<AdequacyReviewDto> {
    this.assertValidReviewId(reviewId);
    return this.command(user =>
      this.httpApi.post(
        `/users/${user.id}/adequacy-review/${reviewId}/confirm-unchanged`,
        {},
      ),
    );
  }

  /** Déclare une évolution : le serveur ouvre les parcours du foyer sans clore la campagne. */
  declareChange(reviewId: number): Observable<AdequacyReviewDto> {
    this.assertValidReviewId(reviewId);
    return this.command(user =>
      this.httpApi.post(
        `/users/${user.id}/adequacy-review/${reviewId}/declare-change`,
        {},
      ),
    );
  }

  private assertValidReviewId(reviewId: number): void {
    if (!Number.isInteger(reviewId) || reviewId <= 0) {
      throw new Error('AdequacyReviewDao: reviewId must be a positive integer');
    }
  }
}
